/**
 * Non-advisory Overview brief builder (S2 / #113).
 *
 * Prefer client-readable package-brief.v1 (or DealPackage narrative.brief);
 * otherwise build a deterministic rule-based brief from DealPackage + diligence.
 * Never recommend bid / invest / pass — denylist is shared with unit tests.
 */

export const BRIEF_SCHEMA_VERSION = 'package-brief.v1';

/** Patterns that must never appear in Overview template / brief copy. */
export const ADVISORY_DENYLIST = [
  /\brecommend(?:s|ed|ing)?\b.{0,40}\b(?:bid|invest|pass|buy)\b/i,
  /\b(?:bid|invest|pass|buy)\b.{0,40}\brecommend(?:s|ed|ing)?\b/i,
  /\bshould\s+(?:bid|invest|pass|buy)\b/i,
  /\bmust\s+(?:bid|invest|pass|buy)\b/i,
  /\binvest\s+now\b/i,
  /\bbid\s+(?:now|aggressively|higher|lower|more|less)\b/i,
  /\bpass\s+on\s+(?:this|the)\s+(?:deal|package)\b/i,
  /\bwe\s+(?:recommend|advise)\b/i,
  /\badvisory\s+recommendation\b/i,
  /\bbuy\s+(?:this|the)\s+(?:deal|package|asset)\b/i,
];

/** Static Overview chrome copy — kept here so denylist tests cover templates. */
export const OVERVIEW_TEMPLATE_STRINGS = [
  'Overview brief',
  'Non-advisory sniff-check — what looks solid, what is concerning, and what is still unknown.',
  'Looks solid',
  'Concerning',
  'Unknown / open',
  'Citations point at source tabs; open a concern to carry selection context into that view.',
  'No package brief yet — showing a deterministic readout from DealPackage and diligence.',
  'DealPackage unavailable — brief limited to diligence / reference JSON when present.',
  'Jump to source',
];

export function textMatchesAdvisoryDenylist(text) {
  const s = String(text || '');
  if (!s.trim()) return null;
  for (const re of ADVISORY_DENYLIST) {
    if (re.test(s)) return re.source;
  }
  return null;
}

export function collectBriefTexts(brief) {
  const out = [];
  if (!brief || typeof brief !== 'object') return out;
  for (const section of ['strengths', 'concerns', 'unknowns']) {
    const items = Array.isArray(brief[section]) ? brief[section] : [];
    for (const item of items) {
      if (!item) continue;
      if (item.title) out.push(String(item.title));
      if (item.text) out.push(String(item.text));
      if (Array.isArray(item.citations)) {
        for (const c of item.citations) {
          if (typeof c === 'string') out.push(c);
          else if (c?.label) out.push(String(c.label));
          else if (c?.key) out.push(String(c.key));
        }
      }
    }
  }
  if (brief.meta?.disclaimer) out.push(String(brief.meta.disclaimer));
  if (brief.disclaimer) out.push(String(brief.disclaimer));
  return out;
}

/**
 * Returns { ok: true } or { ok: false, hits: [{ text, pattern }] }.
 * Used by Overview unit tests and optional runtime guard.
 */
export function auditNonAdvisory(texts) {
  const hits = [];
  for (const text of texts || []) {
    const pattern = textMatchesAdvisoryDenylist(text);
    if (pattern) hits.push({ text: String(text), pattern });
  }
  return hits.length ? { ok: false, hits } : { ok: true, hits: [] };
}

export function auditBriefNonAdvisory(brief) {
  return auditNonAdvisory([...OVERVIEW_TEMPLATE_STRINGS, ...collectBriefTexts(brief)]);
}

function citation(key, label, sourceTab) {
  return { key, label: label || key, sourceTab: sourceTab || null };
}

function item(id, title, text, citations, extra = {}) {
  return {
    id,
    title,
    text,
    citations: citations || [],
    ...extra,
  };
}

function normalizeItem(raw, fallbackId) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || fallbackId || '');
  const title = String(raw.title || raw.heading || '').trim();
  const text = String(raw.text || raw.summary || raw.body || '').trim();
  if (!title && !text) return null;
  const citations = [];
  const rawCites = raw.citations || raw.sources || [];
  if (Array.isArray(rawCites)) {
    for (const c of rawCites) {
      if (typeof c === 'string') citations.push(citation(c, c, inferSourceTab(c, raw)));
      else if (c && typeof c === 'object') {
        citations.push(
          citation(
            String(c.key || c.path || c.source || c.id || title || fallbackId),
            String(c.label || c.key || c.path || ''),
            c.sourceTab || inferSourceTab(c.key || c.path || '', raw),
          ),
        );
      }
    }
  }
  return {
    id,
    title: title || id,
    text,
    citations,
    sourceTab: raw.sourceTab || citations[0]?.sourceTab || inferSourceTab(id, raw),
    wellId: raw.wellId || raw.selectedWellId || raw.propnum || '',
    kind: raw.kind || '',
  };
}

function inferSourceTab(key, raw = {}) {
  if (raw.sourceTab) return raw.sourceTab;
  const k = String(key || '').toLowerCase();
  if (k.includes('well') || k.includes('concentration') || k.includes('boswell')) return 'wells';
  if (k.includes('cash') || k.includes('loe') || k.includes('nocf')) return 'cashflow';
  if (k.includes('reserve') || k.includes('pv10') || k.includes('pbp')) return 'reserves';
  if (k.includes('map') || k.includes('acre')) return 'map';
  if (k.includes('land') || k.includes('hbp')) return 'land';
  if (k.includes('decision') || k.includes('aro') || k.includes('bidprocess')) return 'decision';
  if (k.includes('sensitivity') || k.includes('netback') || k.includes('hh')) return 'sensitivity';
  if (k.includes('geo') || k.includes('las') || k.includes('log')) return 'geoscience';
  if (k.includes('exec')) return 'executive';
  return 'diligence';
}

/** Normalize package-brief.v1 (or narrative.brief) into a stable Overview shape. */
export function normalizePackageBrief(raw, { source = 'package-brief' } = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw.brief && typeof raw.brief === 'object' ? raw.brief : raw;
  const strengths = (body.strengths || []).map((x, i) => normalizeItem(x, `S${i + 1}`)).filter(Boolean);
  const concerns = (body.concerns || []).map((x, i) => normalizeItem(x, `C${i + 1}`)).filter(Boolean);
  const unknowns = (body.unknowns || []).map((x, i) => normalizeItem(x, `U${i + 1}`)).filter(Boolean);
  if (!strengths.length && !concerns.length && !unknowns.length) return null;
  const metaIn = body.meta || raw.meta || {};
  return {
    schema: BRIEF_SCHEMA_VERSION,
    source,
    strengths,
    concerns,
    unknowns,
    meta: {
      dealId: metaIn.dealId || null,
      asOf: metaIn.asOf || metaIn.generatedAt || null,
      generatorVersion: metaIn.generatorVersion || metaIn.modelVersion || null,
      model: metaIn.model || metaIn.generator || source,
      createdAt: metaIn.createdAt || null,
      disclaimer:
        metaIn.disclaimer ||
        'Non-advisory brief — facts and open questions only; not a bid or invest recommendation.',
    },
  };
}

function boswellShare(dealPackage) {
  const assets = Array.isArray(dealPackage?.assets) ? dealPackage.assets : [];
  if (!assets.length) return null;
  const total = assets.reduce((s, a) => s + (Number(a?.ref?.pv10) || 0), 0);
  if (!(total > 0)) return null;
  const boswell = assets.find((a) => a.isBoswell) || assets[0];
  const share = (Number(boswell?.ref?.pv10) || 0) / total;
  return {
    share,
    lease: boswell?.lease || boswell?.propnum || 'lead well',
    propnum: boswell?.propnum || '',
  };
}

/**
 * Deterministic rule-based brief from DealPackage + diligence (+ optional executive).
 * Copy is descriptive only — never advisory.
 */
export function buildRuleBasedBrief({
  dealPackage = null,
  diligence = null,
  executive = null,
  absScreenGaps = null,
} = {}) {
  const strengths = [];
  const concerns = [];
  const unknowns = [];

  const meta = dealPackage?.meta || diligence?.meta || executive?.meta || {};
  const dealId = meta.dealId || null;
  const asOf = dealPackage?.provenance?.asOf || meta.generatedAt || meta.imDate || null;

  if (meta.package || meta.seller) {
    strengths.push(
      item(
        'S-package',
        'Operated package framed in VDR',
        `${meta.seller || 'Seller'} · ${meta.package || 'package'}${
          Array.isArray(meta.counties) && meta.counties.length
            ? ` · ${meta.counties.join(' / ')}`
            : ''
        }. Effective ${meta.effectiveDate || '—'}; bids due ${meta.bidsDue || '—'}.`,
        [
          citation('dealPackage.meta', 'DealPackage meta', 'executive'),
          citation('diligence.bidProcess', 'Bid instructions', 'diligence'),
        ],
        { sourceTab: 'executive', kind: 'package' },
      ),
    );
  }

  const validation = dealPackage?.validation;
  if (validation?.refPdpPv10MM != null) {
    const caseSource = dealPackage?.meta?.caseSource || dealPackage?.cases?.[0]?.caseSource;
    const firm =
      dealPackage?.meta?.primaryFirm ||
      (dealPackage?.cases || []).find((c) => c.caseSource === 'independentEngineer')?.firm;
    const iePrimary = caseSource === 'independentEngineer' && firm;
    strengths.push(
      item(
        'S-engineer-case',
        iePrimary ? 'Independent engineer case primary' : 'Seller reserve-report PV10 reference',
        iePrimary
          ? `Independent engineer (${firm}) case is primary; model/cfScale is challenger. Reference PDP PV10 $${Number(validation.refPdpPv10MM).toFixed(2)}MM.`
          : `Seller reserve-report PV10 $${Number(validation.refPdpPv10MM).toFixed(2)}MM is on the DealPackage as a reference value. The model stream is calibrated to it; no third-party engineer case is loaded.`,
        [citation('dealPackage.validation.refPdpPv10MM', 'validation.refPdpPv10MM', 'reserves')],
        { sourceTab: 'reserves', kind: 'reserves' },
      ),
    );
  }

  const products = meta.products || [];
  const commodity = String(meta.commodity || '');
  const multiProduct =
    (Array.isArray(products) &&
      products.some((p) => /casinghead|residue|gas|ngl/i.test(String(p)))) ||
    commodity.includes('gas');
  if (multiProduct && (meta.primaryProduct || meta.product) === 'oil') {
    strengths.push(
      item(
        'S-multi-product',
        'Multi-product streams on DealPackage',
        `Oil-primary package declares ${
          Array.isArray(products) && products.length
            ? products.join(' / ')
            : commodity || 'oil+gas'
        } with separate nets; WTI and HH are first-class stress inputs.`,
        [citation('dealPackage.meta.products', 'DealPackage products', 'sensitivity')],
        { sourceTab: 'sensitivity', kind: 'commodity' },
      ),
    );
  }

  const kpis = executive?.kpis;
  if (kpis?.hbpPct != null && kpis.hbpPct >= 0.9) {
    strengths.push(
      item(
        'S-hbp',
        'Acreage largely held by production',
        `Reported HBP share ${(kpis.hbpPct * 100).toFixed(1)}% · ${Number(kpis.netAcres || 0).toLocaleString()} net acres.`,
        [citation('executive.kpis.hbpPct', 'Executive KPIs', 'land')],
        { sourceTab: 'land', kind: 'land' },
      ),
    );
  }

  const conc = executive?.concentration;
  const bos = boswellShare(dealPackage);
  const bosShare = conc?.boswellPv10Share ?? bos?.share;
  if (bosShare != null && bosShare >= 0.25) {
    const lease = bos?.lease || 'Lead well';
    concerns.push(
      item(
        'C-concentration',
        'PDP value concentration',
        `${lease} is roughly ${(bosShare * 100).toFixed(0)}% of package PDP PV10 — package value is sensitive to that well’s uptime and decline path.`,
        [
          citation('executive.concentration.boswellPv10Share', 'concentration', 'wells'),
          citation('dealPackage.assets', 'DealPackage assets', 'wells'),
        ],
        {
          sourceTab: 'wells',
          kind: 'concentration',
          wellId: bos?.propnum || '',
        },
      ),
    );
  }

  const risks = diligence?.risks || executive?.risks || [];
  for (const r of risks) {
    if (!r) continue;
    concerns.push(
      item(
        r.id || `C-${r.title}`,
        r.title || 'Diligence risk',
        r.summary || r.text || '',
        [citation(`diligence.risks.${r.id || r.title}`, r.title || 'risk', 'diligence')],
        { sourceTab: 'diligence', kind: 'risk', severity: r.severity },
      ),
    );
  }

  const mid = diligence?.midstreamAssignability;
  if (mid && (mid.overallStatus === 'open' || mid.overallStatus === 'unknown')) {
    const openN = (mid.contracts || []).filter((c) => /open|unknown/i.test(String(c.status || ''))).length;
    concerns.push(
      item(
        'C-midstream',
        'Midstream assignability open',
        mid.overallNote ||
          `Midstream / gathering assignability remains ${mid.overallStatus}${openN ? ` · ${openN} open contracts` : ''}.`,
        [citation('diligence.midstreamAssignability', 'Midstream register', 'diligence')],
        { sourceTab: 'diligence', kind: 'midstream' },
      ),
    );
  }

  const aro = dealPackage?.assumptions?.aroMM;
  if (aro == null || Number(aro) === 0) {
    unknowns.push(
      item(
        'U-aro',
        'ARO / plug liability sizing',
        'Package ARO adder is unset or zero on desk assumptions — confirm plug/abandonment policy and liability schedule separately from package PV.',
        [citation('dealPackage.assumptions.aroMM', 'assumptions.aroMM', 'decision')],
        { sourceTab: 'decision', kind: 'aro' },
      ),
    );
  }

  // ABS concerns from approved freeze run (preferred) or latest durable desk run.
  // Never recompute share/threshold in JS; never render screenStatus as a badge.
  const gapCodes = Array.isArray(absScreenGaps)
    ? absScreenGaps
    : Array.isArray(absScreenGaps?.codes)
      ? absScreenGaps.codes
      : [];
  const absCopy = {
    concentration_single_well:
      'Single-well collateral share exceeds the desk threshold on the ABS screen.',
    concentration_top3: 'Top-three well collateral share exceeds the desk threshold on the ABS screen.',
    aro_unknown: 'ARO is unknown on the ABS screen — note sizing withheld until funded or treated-as-zero.',
    aro_desk_estimate: 'ARO for one or more wells is a desk estimate (JUDGMENT) on the ABS screen.',
  };
  for (const code of gapCodes) {
    const key = typeof code === 'string' ? code : code?.code;
    if (!key || !absCopy[key]) continue;
    if (key === 'aro_unknown' || key === 'aro_desk_estimate') {
      unknowns.push(
        item(`U-abs-${key}`, 'ABS ARO concern', absCopy[key], [citation('abs.screenGaps', key, 'abs')], {
          sourceTab: 'abs',
          kind: 'abs',
          absCode: key,
        }),
      );
    } else {
      concerns.push(
        item(`C-abs-${key}`, 'ABS concentration concern', absCopy[key], [citation('abs.screenGaps', key, 'abs')], {
          sourceTab: 'abs',
          kind: 'abs',
          absCode: key,
        }),
      );
    }
  }
  const absWells = Array.isArray(absScreenGaps?.wells) ? absScreenGaps.wells : [];
  for (const w of absWells) {
    if (!w || String(w.resCat || '') !== '6TA') continue;
    concerns.push(
      item(
        `C-abs-6ta-${w.wellId || w.id || 'well'}`,
        'Temporarily abandoned well in collateral',
        `Well ${w.wellId || w.id || ''} is coded 6TA — costs and ARO remain; revenue is excluded.`,
        [citation('abs.collateral', 'ABS collateral', 'abs')],
        { sourceTab: 'abs', kind: 'abs', absCode: '6TA', wellId: w.wellId || w.id || '' },
      ),
    );
  }

  const vdrStatus = diligence?.vdrStatus || [];
  for (const row of vdrStatus) {
    if (!row) continue;
    if (!/missing|partial|open|tbd|coming/i.test(String(row.status || ''))) continue;
    unknowns.push(
      item(
        `U-vdr-${row.section || row.status}`,
        row.section || 'VDR gap',
        `${row.status}${row.note ? ` — ${row.note}` : ''}`,
        [citation('diligence.vdrStatus', 'VDR status', 'diligence')],
        { sourceTab: 'diligence', kind: 'vdr' },
      ),
    );
  }

  if (!unknowns.length && !concerns.length && !strengths.length) {
    unknowns.push(
      item(
        'U-data',
        'Brief inputs incomplete',
        'DealPackage and diligence payloads are missing or empty in this session — open authenticated Data Room data to populate the Overview.',
        [citation('overview', 'Overview', 'overview')],
        { sourceTab: 'diligence', kind: 'data' },
      ),
    );
  }

  return {
    schema: BRIEF_SCHEMA_VERSION,
    source: 'rule-based',
    strengths,
    concerns,
    unknowns,
    meta: {
      dealId,
      asOf,
      generatorVersion: 'overviewBrief.rule.1',
      model: 'deterministic-rules',
      createdAt: null,
      disclaimer:
        'Non-advisory brief — facts and open questions only; not a bid or invest recommendation.',
    },
  };
}

/**
 * Resolve Overview brief: explicit package brief → DealPackage narrative.brief → rules.
 */
export function buildOverviewBrief({
  packageBrief = null,
  dealPackage = null,
  diligence = null,
  executive = null,
  absScreenGaps = null,
} = {}) {
  const fromFile = normalizePackageBrief(packageBrief, { source: 'package-brief' });
  if (fromFile) return fromFile;

  const narrative = dealPackage?.narrative?.brief || dealPackage?.narrative;
  const fromNarrative = normalizePackageBrief(narrative, { source: 'dealPackage.narrative.brief' });
  if (fromNarrative) return fromNarrative;

  return buildRuleBasedBrief({ dealPackage, diligence, executive, absScreenGaps });
}
